import React, { createContext, Component } from 'react';

// Create the context
export const MultiTabContext = createContext({
  multiTabDetected: false,
  multiTabWarningOpen: false,
  isDismissing: false,
  iconPosition: null,
  dismissWarning: () => {},
  showWarning: () => {},
  setIconRef: () => {},
});

/**
 * MultiTabProvider - Detects when the portal is open in multiple browser tabs/windows
 * Uses BroadcastChannel API for cross-tab communication
 * Shows warning dialog when multiple tabs are detected
 */
export class MultiTabProvider extends Component {
  constructor(props) {
    super(props);
    
    // Generate unique tab ID
    this.tabId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.activeTabIds = new Set([this.tabId]);
    this.channel = null;
    this.hasRespondedToTab = new Set(); // Track which tabs we've already responded to
    
    this.state = {
      multiTabDetected: false,
      multiTabWarningOpen: false,
      isDismissing: false,
      iconPosition: null,
    };
    this.iconRef = null;
  }

  componentDidMount() {
    this.initBroadcastChannel();
  }

  componentWillUnmount() {
    this.cleanup();
  }

  initBroadcastChannel = () => {
    try {
      // Check if BroadcastChannel is supported
      if (typeof window.BroadcastChannel === 'undefined') {
        console.warn('BroadcastChannel API not supported in this browser');
        return;
      }

      this.channel = new window.BroadcastChannel('portal-tab-detection');

      // Listen for messages from other tabs
      this.channel.onmessage = (event) => {
        const { type, tabId } = event.data;

        if (type === 'tab-join' && tabId !== this.tabId) {
          // Another tab joined - add to our set
          this.activeTabIds.add(tabId);
          
          // Respond with our own tab-join so the new tab knows about us
          // Only respond once per tab to avoid infinite loops
          if (!this.hasRespondedToTab.has(tabId)) {
            this.hasRespondedToTab.add(tabId);
            this.channel.postMessage({
              type: 'tab-join',
              tabId: this.tabId,
            });
          }

          // If we now have multiple tabs, show warning
          if (this.activeTabIds.size > 1) {
            this.setState({
              multiTabDetected: true,
              multiTabWarningOpen: true,
            });
          }
        } else if (type === 'tab-leave' && tabId !== this.tabId) {
          // Another tab left - remove from our set
          this.activeTabIds.delete(tabId);
          this.hasRespondedToTab.delete(tabId);

          // If we're back to single tab, refresh the page
          if (this.activeTabIds.size === 1) {
            window.location.reload();
          }
        }
      };

      // Announce our presence to other tabs
      this.channel.postMessage({
        type: 'tab-join',
        tabId: this.tabId,
      });

      // Also handle beforeunload to notify other tabs when this tab closes
      window.addEventListener('beforeunload', this.handleBeforeUnload);
      
    } catch (error) {
      console.error('Error initializing BroadcastChannel:', error);
    }
  };

  handleBeforeUnload = () => {
    if (this.channel) {
      this.channel.postMessage({
        type: 'tab-leave',
        tabId: this.tabId,
      });
    }
  };

  cleanup = () => {
    // Notify other tabs that we're leaving
    if (this.channel) {
      try {
        this.channel.postMessage({
          type: 'tab-leave',
          tabId: this.tabId,
        });
        this.channel.close();
      } catch (error) {
        // Channel might already be closed
      }
    }

    // Remove event listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  };

  setIconRef = (ref) => {
    this.iconRef = ref;
  };

  dismissWarning = () => {
    // Calculate icon position and dialog center for animation
    let iconPosition = null;
    if (this.iconRef) {
      const iconRect = this.iconRef.getBoundingClientRect();
      // Get the dialog element
      const dialogPaper = document.querySelector('[role="dialog"]');
      if (dialogPaper) {
        const dialogRect = dialogPaper.getBoundingClientRect();
        // Calculate offset from dialog center to icon center
        const iconCenterX = iconRect.left + iconRect.width / 2;
        const iconCenterY = iconRect.top + iconRect.height / 2;
        const dialogCenterX = dialogRect.left + dialogRect.width / 2;
        const dialogCenterY = dialogRect.top + dialogRect.height / 2;
        iconPosition = {
          translateX: iconCenterX - dialogCenterX,
          translateY: iconCenterY - dialogCenterY,
        };
      }
    }
    // Start dismissing animation with icon position
    this.setState({ isDismissing: true, iconPosition });
    // After animation completes, close the dialog
    setTimeout(() => {
      this.setState({ multiTabWarningOpen: false, isDismissing: false, iconPosition: null });
    }, 500);
  };

  showWarning = () => {
    this.setState({ multiTabWarningOpen: true });
  };

  render() {
    const { children } = this.props;
    const { multiTabDetected, multiTabWarningOpen, isDismissing, iconPosition } = this.state;

    return (
      <MultiTabContext.Provider
        value={{
          multiTabDetected,
          multiTabWarningOpen,
          isDismissing,
          iconPosition,
          dismissWarning: this.dismissWarning,
          showWarning: this.showWarning,
          setIconRef: this.setIconRef,
        }}
      >
        {children}
      </MultiTabContext.Provider>
    );
  }
}

export default MultiTabContext;




// WEBPACK FOOTER //
// ./src/context/MultiTabContext.js